import React from 'react';
import {
  KpiCard,
  ChartCard,
  formatNumber,
  formatPercent,
  formatCurrency,
  convertFromUsd,
} from '../components/ChartKit';

/**
 * SEO & Landing Page Analytics Tab
 * Analyzes landing page conversion, form submissions, inquiry subject distribution,
 * and SEO/AIEO content pipeline metrics without hardcoding.
 */
const SeoLandingTab = ({ data, dashboard, periodLabel, currency = 'USD' }) => {
  const t = data?.totals || {};
  const subjects = data?.subjectAnalysis || [];
  const pages = data?.landingPages || [];
  const seo = data?.seoSummary || {};

  const rates = dashboard?.sections?.overview?.fx?.rates || { USD: 1 };
  const shown = (usd) => formatCurrency(convertFromUsd(usd, currency, rates), currency);

  return (
    <div className="analytics-tab-body">
      {/* ── KPI Cards ────────────────────────────────────────────────────────── */}
      <div className="analytics-kpis">
        <KpiCard
          icon="fa-pager"
          label="Landing page leads"
          value={formatNumber(t.totalLeads)}
          hint={
            t.leadSharePercent
              ? `${formatPercent(t.leadSharePercent)} of all new leads`
              : periodLabel
          }
          tone="blue"
        />
        <KpiCard
          icon="fa-inbox"
          label="Form submissions"
          value={formatNumber(t.totalSubmissions)}
          hint={
            t.submissionToLeadRate !== null
              ? `${formatPercent(t.submissionToLeadRate)} conversion rate`
              : 'Captured submissions'
          }
          tone="violet"
        />
        <KpiCard
          icon="fa-comments"
          label="Top inquiry subject"
          value={t.topSubject || 'None'}
          hint="Most selected topic"
          tone="amber"
        />
        <KpiCard
          icon="fa-trophy"
          label="Won revenue"
          value={shown(t.wonRevenueUsd)}
          hint={`${formatNumber(t.wonDeals)} won deal(s)`}
          tone="green"
        />
        <KpiCard
          icon="fa-globe"
          label="SEO content published"
          value={formatNumber(t.seoPublishedBriefsCount)}
          hint={`${formatNumber(t.seoTopicsCount)} topic cluster(s)`}
          tone="cyan"
        />
      </div>

      {/* ── Analytics Grid ───────────────────────────────────────────────────── */}
      <div className="analytics-grid">
        {/* Form Subject Analysis (Hero Card) */}
        <ChartCard
          wide
          title="Inquiry Subject Analysis"
          subtitle="Form subjects selected by visitors and their progression through the pipeline"
          empty={!subjects.length}
          emptyText="No landing page form submissions recorded yet."
        >
          {subjects.length > 0 && (
            <div className="analytics-seo-subject-wrap">
              {/* Horizontal Bar Breakdown */}
              <div style={{ marginBottom: 24 }}>
                <ul className="analytics-bars">
                  {subjects.map((s) => (
                    <li key={s.subject}>
                      <span className="analytics-bars-label" title={s.subject}>
                        <strong>{s.subject}</strong>
                      </span>
                      <span className="analytics-bars-track" aria-hidden="true">
                        <span
                          className="analytics-bars-fill"
                          style={{
                            width: `${Math.min(100, Math.max(4, s.percentage))}%`,
                            background: '#7c3aed',
                          }}
                        />
                      </span>
                      <span className="analytics-bars-value">
                        {formatNumber(s.count)}{' '}
                        <span style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>
                          ({formatPercent(s.percentage)})
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Detailed Subject Conversion Table */}
              <div className="analytics-table-wrap">
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>Inquiry Subject</th>
                      <th className="num">Total Leads</th>
                      <th className="num">Share</th>
                      <th className="num">New</th>
                      <th className="num">Qualified+</th>
                      <th className="num">Won Deals</th>
                      <th className="num">Won Revenue</th>
                      <th className="num">Conversion %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subjects.map((s) => (
                      <tr key={s.subject}>
                        <td>
                          <strong>{s.subject}</strong>
                        </td>
                        <td className="num">{formatNumber(s.count)}</td>
                        <td className="num">{formatPercent(s.percentage)}</td>
                        <td className="num">{formatNumber(s.newCount)}</td>
                        <td className="num">{formatNumber(s.qualifiedOrBetter)}</td>
                        <td className="num">{formatNumber(s.wonCount)}</td>
                        <td className="num">{shown(s.revenueUsd)}</td>
                        <td className="num">{formatPercent(s.conversionRate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </ChartCard>

        {/* Landing Pages Table */}
        <ChartCard
          title="Landing Pages"
          subtitle="Pages configured in your workspace and their capture activity"
          empty={!pages.length}
          emptyText="No landing pages created yet."
        >
          {pages.length > 0 && (
            <div className="analytics-table-wrap">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Page Name</th>
                    <th>Status</th>
                    <th>Published</th>
                    <th className="num">Submissions</th>
                    <th className="num">Leads</th>
                  </tr>
                </thead>
                <tbody>
                  {pages.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <strong>{p.name}</strong>
                        <div style={{ fontSize: 11, color: '#64748b' }}>/{p.slug}</div>
                      </td>
                      <td>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 600,
                            background: p.status === 'active' ? '#ecfdf5' : '#f1f5f9',
                            color: p.status === 'active' ? '#047857' : '#64748b',
                          }}
                        >
                          {p.status}
                        </span>
                      </td>
                      <td>
                        {p.isApproved ? (
                          <span style={{ color: '#059669', fontSize: 12, fontWeight: 600 }}>
                            <i className="fas fa-check-circle" style={{ marginRight: 4 }} />
                            Live
                          </span>
                        ) : (
                          <span style={{ color: '#64748b', fontSize: 12 }}>Draft</span>
                        )}
                      </td>
                      <td className="num">{formatNumber(p.submissionsCount)}</td>
                      <td className="num">{formatNumber(p.leadsCount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ChartCard>

        {/* SEO & Content Engine */}
        <ChartCard
          title="SEO & Content Engine"
          subtitle="Content pipeline and search visibility metrics"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 12,
              }}
            >
              <div
                style={{
                  background: '#f8fafc',
                  padding: 14,
                  borderRadius: 10,
                  border: '1px solid #e2e8f0',
                }}
              >
                <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                  Topic Clusters
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginTop: 4 }}>
                  {formatNumber(seo.topicsCount)}
                </div>
              </div>
              <div
                style={{
                  background: '#f8fafc',
                  padding: 14,
                  borderRadius: 10,
                  border: '1px solid #e2e8f0',
                }}
              >
                <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                  Content Briefs
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginTop: 4 }}>
                  {formatNumber(seo.briefsCount)}
                  <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 500, marginLeft: 6 }}>
                    ({formatNumber(seo.publishedBriefsCount)} approved)
                  </span>
                </div>
              </div>
              <div
                style={{
                  background: '#f8fafc',
                  padding: 14,
                  borderRadius: 10,
                  border: '1px solid #e2e8f0',
                }}
              >
                <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                  Average Audit Score
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginTop: 4 }}>
                  {seo.avgAuditScore !== null ? `${seo.avgAuditScore}/100` : '—'}
                </div>
              </div>
              <div
                style={{
                  background: '#f8fafc',
                  padding: 14,
                  borderRadius: 10,
                  border: '1px solid #e2e8f0',
                }}
              >
                <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                  Visibility Score
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginTop: 4 }}>
                  {seo.visibilityScore !== null ? `${seo.visibilityScore}%` : '—'}
                </div>
              </div>
            </div>

            <ul className="analytics-notes">
              <li>
                <i className="fas fa-info-circle" style={{ color: '#2563eb', marginRight: 6 }} />
                <span>
                  Leads captured from landing pages automatically link with CRM pipeline and
                  Attribution.
                </span>
              </li>
              <li>
                <i className="fas fa-check-double" style={{ color: '#16a34a', marginRight: 6 }} />
                <span>
                  Inbound subject options provide visitor intent categorization without manual
                  tagging.
                </span>
              </li>
            </ul>
          </div>
        </ChartCard>
      </div>
    </div>
  );
};

export default SeoLandingTab;
